import React, { useEffect, useRef } from "react";

/**
 * A highly performant, hardware-accelerated parallax component.
 * Uses requestAnimationFrame and scroll listeners to calculate the element's
 * position relative to the viewport center and apply a smooth translate3d transform.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The element to apply parallax to
 * @param {number} [props.speed=0.1] - Speed of the parallax effect. Negative values move in reverse.
 * @param {string} [props.className=""] - Additional CSS classes
 * @param {boolean} [props.scaleOnBackground=true] - If true, applies a subtle scale to background images to prevent edge gaps
 * @param {string} [props.type="text"] - "text" or "background" to apply default optimized styles
 */
export default function Parallax({
  children,
  speed = 0.1,
  className = "",
  scaleOnBackground = true,
  type = "text",
}) {
  const elementRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    let animationFrameId;

    const handleScroll = () => {
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Only calculate and apply transform if the element is visible in the viewport
      if (rect.bottom >= 0 && rect.top <= viewportHeight) {
        const elementCenter = rect.top + rect.height / 2;
        const viewportCenter = viewportHeight / 2;
        
        // Calculate distance from viewport center
        const distanceFromCenter = elementCenter - viewportCenter;
        
        // Calculate translation based on speed
        const translateY = distanceFromCenter * speed;

        // Apply hardware-accelerated transform
        if (type === "background" && scaleOnBackground) {
          element.style.transform = `translate3d(0, ${translateY}px, 0) scale(1.15)`;
        } else {
          element.style.transform = `translate3d(0, ${translateY}px, 0)`;
        }
      }
    };

    const onScroll = () => {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(handleScroll);
    };

    // Run once initially to set correct position
    handleScroll();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [speed, type, scaleOnBackground]);

  // Set initial styles for smooth rendering and hardware acceleration
  const initialStyle = {
    willChange: "transform",
    transition: "transform 0.1s cubic-bezier(0.25, 1, 0.5, 1)", // smooth out scroll increments
    transform: type === "background" && scaleOnBackground ? "scale(1.15)" : "none",
  };

  return (
    <div ref={elementRef} style={initialStyle} className={className}>
      {children}
    </div>
  );
}
